<template>
  <div
    :class="['quiz', { 'bp-xl': viewportWidth >= 1440 }]"
    v-if="quizData && quizData.cards?.length"
  >
    <StartScreen
      v-if="!quizStarted && !quizAlreadyCompleted"
      :username="username"
      :userHasGus="userHasGus"
      :userLabel="userLabel"
      :quizData="quizData"
      @advance="advance"
    ></StartScreen>
    <div class="game">
      <ol v-if="quizStarted || quizAlreadyCompleted">
        <li
          v-for="(card, index) in cards.slice(0, activeRoundIndex + 1)"
          :key="index"
        >
          <Card
            v-if="index <= activeRoundIndex"
            @answer-selected="handleValidity"
            @next-clicked="advance"
            :numQuestions="numQuestions"
            :cardIndex="index"
            :question="card.question"
            :answers="card.answers"
            :explainer="card.explainer"
            :correctAnswerIndex="correctAnswers[index]"
            :selectedAnswer="answers[index]"
            :current="index === activeRoundIndex"
            :totalScore="score"
            :quizAlreadyCompleted="quizAlreadyCompleted"
          ></Card>
        </li>
      </ol>
    </div>
    <Result
      v-if="allQuestionsAnswered"
      :myslateDomain="myslateDomain"
      :username="username"
      :userLabel="userLabel"
      :hasLeaderboardAccess="hasLeaderboardAccess"
      :quizId="quizId"
      :quizData="quizData"
      :numQuestions="numQuestions"
      :numCorrect="numCorrect"
      :score="score"
      :answers="answers"
      :quizAlreadyCompleted="quizAlreadyCompleted"
      :quizCompletedAsNonPlus="quizCompletedAsNonPlus"
    ></Result>
  </div>
  <div class="quiz-error-container" v-else>
    <p class="slate-notification--error" v-if="quizError">
      Sorry! We couldn't display your quiz at this moment. Please try again
      later.
    </p>
  </div>
</template>

<script>
import Card from "@/components/Card.vue";
import StartScreen from "@/components/StartScreen.vue";
import Result from "@/components/Result.vue";
import {
  initPromise,
  getLocalStorageQuiz,
  removeLocalStorageQuiz,
  isShasha,
  pymChild,
  getParentPositionInfo,
} from "../utils.js";
import { INITIAL_TIME } from "@/constants.js";

export default {
  name: "Quiz",
  components: {
    Card,
    StartScreen,
    Result,
  },
  data() {
    return {
      myslateDomain: import.meta.env.VITE_MYSLATE_DOMAIN,
      quizId: "",
      quizData: "",
      userLabel: "",
      userHasGus: false,
      username: "",
      hasLeaderboardAccess: false,
      correctAnswers: [],

      quizStarted: false,
      quizInProgress: false,
      quizAlreadyCompleted: false,
      quizCompletedAsNonPlus: false,
      activeRoundIndex: -1,
      activeCards: [],

      numCorrect: 0,
      score: 0,
      answers: [],

      quizError: false,
      viewportWidth: 0,
    };
  },
  computed: {
    allQuestionsAnswered() {
      return (
        this.answers.length &&
        this.answers.length === this.numQuestions &&
        !this.quizInProgress
      );
    },
    cards() {
      return this.quizData.cards;
    },
    numQuestions() {
      // ensure quizData has loaded (avoid null error upon mount)
      if (this.cards) {
        return this.cards.length;
      }
      return 0;
    },
  },
  methods: {
    handleValidity(answerData) {
      this.quizInProgress = false;
      this.score += answerData.questionScore;
      this.answers.push({
        answer: answerData.selectedAnswerIndex,
        score: answerData.questionScore,
        time: INITIAL_TIME - answerData.questionScore,
      });

      // this is getting messy, clean up later
      if (answerData.questionScore > 0) this.numCorrect++;
    },
    advance() {
      if (!this.quizStarted) {
        this.quizStarted = true;
      }
      this.activeRoundIndex++;
      if (this.activeRoundIndex !== this.numQuestions) {
        // begin next round (if there is one)
        this.quizInProgress = true;
        this.activeCards[this.activeRoundIndex] = false;
        this.activeCards[this.activeRoundIndex + 1] = true;

        // Amplitude
        let amplitudeData = {
          eventName: "Quiz - Start",
        };

        if (this.activeRoundIndex !== 0) {
          amplitudeData = {
            eventName: "Quiz - Next",
            properties: {
              "Completed Question": this.activeRoundIndex,
            },
          };
        }

        pymChild.sendMessage("tracking", JSON.stringify(amplitudeData));
      }
    },
    setResultsInfo(quizData) {
      this.numCorrect = quizData.answers.reduce((total, current, i) => {
        if (current.score > 0 && current.answer === this.correctAnswers[i]) {
          return total + 1;
        }
        return total;
      }, 0);

      this.score = quizData.score;
      this.answers = quizData.answers;
      this.quizAlreadyCompleted = true;
      this.activeRoundIndex = this.cards.length;
    },
    fetchQuizInfo() {
      const options = {
        method: "GET",
        credentials: "include", //pass cookies
        headers: {
          Accept: "application/json",
        },
      };

      return fetch(
        this.myslateDomain + "/quizzes/quiz/" + this.quizId + ".json/",
        options
      )
        .then((response) => response.json())
        .then((response) => {
          this.quizData = response;
          this.correctAnswers = response.cards.map((card) =>
            card.answers.findIndex((v) => v.validity === true)
          );
          if (response.user) {
            this.setResultsInfo(response.user);
            removeLocalStorageQuiz(this.quizId);
          } else if (getLocalStorageQuiz(this.quizId)) {
            const localQuizData = JSON.parse(getLocalStorageQuiz(this.quizId));
            this.setResultsInfo(localQuizData);
            this.quizCompletedAsNonPlus = true;
          }
        })
        .catch((error) => {
          console.warn("Error fetching quiz:", error);
          this.quizError = true;
        });
    },
  },
  async mounted() {
    if (window !== window.top) {
      const { viewportWidth } = await getParentPositionInfo();
      this.viewportWidth = viewportWidth;
    }
    const initData = await initPromise;
    this.quizId = initData.slug;

    if (initData.user) {
      this.username = initData.user.display_name;
      this.hasLeaderboardAccess = !!initData.user.has_leaderboard_access;
      if (isShasha(initData.user.uid)) {
        this.userLabel = "Slate’s IT Witch";
        this.userHasGus = true;
      } else if (initData.user.is_plus) {
        this.userLabel = "Slate Plus Member";
      }
    }

    await this.fetchQuizInfo();

    this._keyListener = function (e) {
      if (e.which === 13 && !this.quizStarted) {
        this.advance();
      }
    };
    window.addEventListener("keydown", this._keyListener.bind(this));
  },
  // nice job removing your event handlers becky!
  beforeUnmount() {
    window.removeEventListener("keydown", this._keyListener);
  },
};
</script>

<style lang="scss">
@import "@/styles/fonts.scss";

.quiz {
  display: flow-root;
  background-color: var(--interactive-bg-color);
  border: 1px solid var(--interactive-border-color);
  border-radius: 4px;
  padding: 1rem 0 0;
}

.game {
  padding: 0 1rem;
}

.bp-xl .game {
  padding: 0 8rem;
}

ol {
  list-style-type: none;
  margin: 0;
  padding: 0;
}

.quiz-error-container {
  display: block;
  padding: 1rem;
  background-color: var(--interactive-bg-color);
}
</style>
